import { useRef, useState, useCallback, useEffect } from 'react';

type Point = [number, number];

interface DrawingCanvasProps {
  /** Container dimensions for coordinate calculation */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Called when drawing finishes. Coords are relative to the container (pixels). */
  onDrawEnd: (coords: Point[]) => void;
  /** Called on single click (no drag). Coords are relative to the container (pixels). */
  onClick?: (x: number, y: number) => void;
  /** Called when drawing starts (mousedown + drag) */
  onDrawStart?: () => void;
  strokeColor?: string;
  strokeWidth?: number;
}

/**
 * Transparent canvas overlay for freehand drawing.
 * Uses getBoundingClientRect for coordinates — no offsetLeft dependency.
 */
export function DrawingCanvas({
  containerRef,
  onDrawEnd,
  onClick,
  onDrawStart,
  strokeColor = '#e32d4b',
  strokeWidth = 4,
}: DrawingCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isPainting = useRef(false);
  const lastPos = useRef<Point | null>(null);
  const coordsRef = useRef<Point[]>([]);
  const hasDragged = useRef(false);

  const getRelativeCoords = useCallback(
    (e: React.MouseEvent | MouseEvent): Point | null => {
      const container = containerRef.current;
      if (!container) return null;
      const rect = container.getBoundingClientRect();
      return [e.clientX - rect.left, e.clientY - rect.top];
    },
    [containerRef],
  );

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  // Sync canvas size with container
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const observer = new ResizeObserver(() => {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    });
    observer.observe(container);
    // Initial size
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;

    return () => observer.disconnect();
  }, [containerRef]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const pos = getRelativeCoords(e);
      if (!pos) return;
      isPainting.current = true;
      hasDragged.current = false;
      lastPos.current = pos;
      coordsRef.current = [pos];
    },
    [getRelativeCoords],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPainting.current) return;
      const pos = getRelativeCoords(e);
      if (!pos || !lastPos.current) return;

      if (!hasDragged.current) {
        hasDragged.current = true;
        onDrawStart?.();
      }
      coordsRef.current.push(pos);

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      ctx.strokeStyle = strokeColor;
      ctx.lineJoin = 'round';
      ctx.lineWidth = strokeWidth;
      ctx.beginPath();
      ctx.moveTo(lastPos.current[0], lastPos.current[1]);
      ctx.lineTo(pos[0], pos[1]);
      ctx.stroke();

      lastPos.current = pos;
    },
    [getRelativeCoords, strokeColor, strokeWidth, onDrawStart],
  );

  const handleMouseUp = useCallback(() => {
    if (!isPainting.current) return;
    isPainting.current = false;

    if (hasDragged.current && coordsRef.current.length > 1) {
      // Filter every 6th point for smoother curves (matching old behavior)
      const filtered = coordsRef.current.length > 10
        ? coordsRef.current.filter((_, i) => i === 0 || i === coordsRef.current.length - 1 || i % 6 === 0)
        : coordsRef.current;
      onDrawEnd(filtered);
    } else if (coordsRef.current.length > 0 && onClick) {
      const [x, y] = coordsRef.current[0];
      onClick(x, y);
    }

    coordsRef.current = [];
    lastPos.current = null;
    clearCanvas();
  }, [onDrawEnd, onClick, clearCanvas]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-[2] cursor-crosshair"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    />
  );
}
